import type { TranslationFunctions } from "@i18n/i18n-types";

export type ShipmentTab = "bundle" | "delivery" | "pickup";

// Bundle capacity shown on the shipment card (Figma "OD - Shipments"). Display only: the backend does not
// enforce a package limit or included/paid packages yet; it reserves one credit per package on confirmation.
export const PACKAGE_SLOTS = 10;
export const INCLUDED_PACKAGES = 5;

/** Recipient-facing grouping of bundle statuses; collected and cancelled bundles are not listed. */
export const TAB_STATUSES = {
	bundle: ["FORMING"],
	delivery: ["CONFIRMED"],
	pickup: ["READY_FOR_PICKUP", "PARTIALLY_COLLECTED"],
} as const satisfies Record<ShipmentTab, readonly string[]>;

export const ACTIVE_STATUSES = [...TAB_STATUSES.bundle, ...TAB_STATUSES.delivery, ...TAB_STATUSES.pickup];

/**
 * "Bundle N°" label: position among the recipient's active bundles, oldest first. Not stored by the
 * backend, so it can shift when a bundle is collected or cancelled. Returns undefined for inactive bundles.
 */
export function bundleOrdinal(bundles: readonly { id: string; createdAt: string }[], bundleId: string): number | undefined {
	const index = [...bundles].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).findIndex((bundle) => bundle.id === bundleId);
	return index >= 0 ? index + 1 : undefined;
}

export function tabOf(status: string): ShipmentTab | null {
	for (const tab of ["bundle", "delivery", "pickup"] as const) {
		if ((TAB_STATUSES[tab] as readonly string[]).includes(status)) return tab;
	}
	return null;
}

type NewUi = TranslationFunctions["new-ui"];

export function bundleStatusLabel(i18n: NewUi, status: string): string {
	switch (status) {
		case "FORMING":
			return i18n.OD_STATUS_FORMING();
		case "CONFIRMED":
			return i18n.OD_STATUS_CONFIRMED();
		case "READY_FOR_PICKUP":
			return i18n.OD_STATUS_READY_FOR_PICKUP();
		case "PARTIALLY_COLLECTED":
			return i18n.OD_STATUS_PARTIALLY_COLLECTED();
		case "COLLECTED":
			return i18n.OD_STATUS_COLLECTED();
		case "CANCELLED":
			return i18n.OD_STATUS_CANCELLED();
		default:
			return status;
	}
}

export function packageStatusLabel(i18n: NewUi, status: string): string {
	switch (status) {
		case "IN_WAREHOUSE":
			return i18n.OD_PKG_IN_WAREHOUSE();
		case "IN_TRANSIT_TO_HUB":
			return i18n.OD_PKG_IN_TRANSIT_TO_HUB();
		case "IN_COMPARTMENT":
			return i18n.OD_PKG_IN_COMPARTMENT();
		case "COLLECTED":
			return i18n.OD_PKG_COLLECTED();
		case "IN_TRANSIT_TO_WAREHOUSE":
			return i18n.OD_PKG_IN_TRANSIT_TO_WAREHOUSE();
		default:
			return status;
	}
}
